import { TKV2_ORDER_ATTACHMENT_API, Tkv2OrderAttachmentBody } from '@aloft/toolkit-shared';

import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { getBaseUrl } from '../../hooks/api/getBaseUrl.ts';
import {
  useGetTkv2AnalyticsAttachmentAPI,
  usePutTkv2AnalyticsAttachmentAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';

/** Sentinel value for startTimeRef when the timer is paused (tab hidden). */
const TIMER_PAUSED = -1;

/**
 * Tracks time spent on each page within an order.
 * It sends the duration to the backend when the user navigates away, closes
 * the tab, refreshes, or switches to another browser tab.
 *
 * Time is accumulated across multiple visits to the same page (not overwritten).
 *
 * For normal in-app navigation, the existing React Query mutation is used.
 * For unload and visibility-change events, a raw fetch with `keepalive: true`
 * is used so the browser guarantees delivery even as the page tears down.
 */
export const PageTimeTracker = () => {
  const orderID = useParams().orderId;
  const location = useLocation();
  const { fetcherOptions } = useToolkitContext();

  const { refetch: refetchAnalytics, data: analyticsResponse } = useGetTkv2AnalyticsAttachmentAPI(
    {
      pathParams: { orderID: orderID! },
    },
    { enabled: !!orderID },
  );
  const { mutate: analyticsMutate } = usePutTkv2AnalyticsAttachmentAPI({
    onSuccess: refetchAnalytics,
  });

  const analytics = analyticsResponse?.body as Tkv2OrderAttachmentBody<'analytics'> | undefined;

  const startTimeRef = useRef<number>(Date.now());
  const currentPathRef = useRef<string>(location.pathname);

  // Cache the latest auth token so it is available synchronously during unload.
  const tokenRef = useRef<string | null>(null);
  useEffect(() => {
    fetcherOptions
      .accessTokenFn?.()
      .then((token) => {
        tokenRef.current = token;
      })
      .catch(console.error);
  });

  // Refs to the latest analytics and fetcherOptions so event-listener callbacks
  // (which close over a stale snapshot) always see the current values.
  const analyticsRef = useRef(analytics);
  const fetcherOptionsRef = useRef(fetcherOptions);
  useEffect(() => {
    analyticsRef.current = analytics;
    fetcherOptionsRef.current = fetcherOptions;
  });

  /** Extracts the page key from a full pathname. */
  const getPageKey = useCallback(
    (pathname: string) => pathname.replace(new RegExp(`^.*/order/${orderID}/`), ''),
    [orderID],
  );

  /**
   * Sends elapsed time for a page using fetch + keepalive so the request
   * survives page unload. Falls back silently if no token is cached yet.
   */
  const flushWithKeepalive = useCallback(
    (page: string, duration: number) => {
      if (!tokenRef.current || !orderID) return;

      const currentAnalytics = analyticsRef.current;
      const { headers } = fetcherOptionsRef.current;

      const body = TKV2_ORDER_ATTACHMENT_API.ANALYTICS.PUT.REQUEST.BODY.parse({
        version: 1,
        pageTimeMs: {
          ...currentAnalytics?.pageTimeMs,
          [page]: (currentAnalytics?.pageTimeMs?.[page] ?? 0) + duration,
        },
      });

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetch(`${getBaseUrl()}/api/v2/orders/${orderID}/attachments/analytics`, {
        method: 'PUT',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenRef.current}`,
          ...headers,
        },
        body: JSON.stringify(body),
      });
    },
    [orderID],
  );

  // When location changes, send time for the previous page via React Query mutation.
  useEffect(() => {
    const previousPath = currentPathRef.current;
    const previousPage = getPageKey(previousPath);
    const now = Date.now();

    // If the timer was paused (tab was hidden when the route changed), there is
    // nothing to flush — the visibility handler already flushed on hide.
    if (startTimeRef.current !== TIMER_PAUSED && orderID) {
      const duration = now - startTimeRef.current;

      if (duration > 1000) {
        analyticsMutate({
          pathParams: { orderID },
          body: TKV2_ORDER_ATTACHMENT_API.ANALYTICS.PUT.REQUEST.BODY.parse({
            version: 1,
            pageTimeMs: {
              ...analytics?.pageTimeMs,
              [previousPage]: (analytics?.pageTimeMs?.[previousPage] ?? 0) + duration,
            },
          }),
        });
      }
    }

    // Reset for the new page.
    startTimeRef.current = now;
    currentPathRef.current = location.pathname;
  }, [location.pathname, fetcherOptions]);

  // Flush on tab close or page refresh (pagehide is more reliable than beforeunload).
  useEffect(() => {
    const handler = () => {
      if (startTimeRef.current === TIMER_PAUSED) return;
      const duration = Date.now() - startTimeRef.current;
      const page = getPageKey(currentPathRef.current);
      if (duration > 1000) flushWithKeepalive(page, duration);
    };

    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, [flushWithKeepalive, getPageKey]);

  // Shared pause/resume handlers used by both visibilitychange and window blur/focus.
  const pauseTimer = useCallback(() => {
    if (startTimeRef.current === TIMER_PAUSED) return;
    const duration = Date.now() - startTimeRef.current;
    const page = getPageKey(currentPathRef.current);
    if (duration > 1000) flushWithKeepalive(page, duration);
    startTimeRef.current = TIMER_PAUSED;
  }, [flushWithKeepalive, getPageKey]);

  const resumeTimer = useCallback(() => {
    if (startTimeRef.current === TIMER_PAUSED) startTimeRef.current = Date.now();
  }, []);

  // Pause the timer when the tab is hidden; resume when it becomes visible again.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'hidden') {
        pauseTimer();
      } else if (document.visibilityState === 'visible') {
        resumeTimer();
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [pauseTimer, resumeTimer]);

  // Pause the timer when the browser window loses focus (e.g. alt+tab to another
  // application); resume when the window regains focus.
  useEffect(() => {
    window.addEventListener('blur', pauseTimer);
    window.addEventListener('focus', resumeTimer);
    return () => {
      window.removeEventListener('blur', pauseTimer);
      window.removeEventListener('focus', resumeTimer);
    };
  }, [pauseTimer, resumeTimer]);

  return null;
};
